import { useMemo, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { money, parseMoney } from '../../lib/format.ts';
import { localMoment, ruleOpen } from '../../lib/menu.ts';
import { usePlaces } from '../../lib/places.tsx';
import type { Catalog, Place, PriceRule } from '../../lib/types.ts';
import { useNow, useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

/** Monday first, the way people read a week here; values are the server's 0 = Sunday. */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const;
const DAY_KEYS = ['day.sun', 'day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat'] as const;
const priceText = (minor: number) => (minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2));

function daysText(days: number[]): string {
  const set = new Set(days);
  if (set.size === 7) return t('price_times.every_day');
  if (set.size === 5 && [1, 2, 3, 4, 5].every((d) => set.has(d))) return t('price_times.weekdays');
  if (set.size === 2 && set.has(0) && set.has(6)) return t('price_times.weekends');
  return WEEK.filter((d) => set.has(d))
    .map((d) => t(DAY_KEYS[d]))
    .join(', ');
}

/**
 * Happy hour and other price times: lower prices on chosen items at chosen hours. They apply
 * on their own to every new order line while they are on; nobody has to remember them.
 */
export function PriceTimes() {
  const { fail, toast } = useUi();
  const { data, reload } = useAction<Catalog & { price_rules: PriceRule[] }>('products.catalog', { include_inactive: true });
  const [editing, setEditing] = useState<PriceRule | 'new' | null>(null);
  const places = usePlaces();
  const now = useNow();
  const moment = data ? localMoment(new Date(now), data.time_zone ?? 'UTC') : null;

  const toggle = async (r: PriceRule, active: boolean) => {
    try {
      await action('products.save_price_rule', { ...r, active });
      toast(active ? t('price_times.switched_on', { name: r.name }) : t('price_times.switched_off', { name: r.name }));
      void reload();
    } catch (err) {
      fail(err);
    }
  };
  const forText = (r: PriceRule) => {
    if (!data) return '';
    const names = [
      ...r.categories.map((id) => data.categories.find((c) => c.id === id)?.name).filter(Boolean),
      ...r.products.map((id) => data.products.find((p) => p.id === id)?.name).filter(Boolean),
    ];
    if (!names.length) return t('price_times.whole_menu');
    return names.length > 3 ? t('price_times.n_more', { first: names.slice(0, 2).join(', '), n: names.length - 2 }) : names.join(', ');
  };

  return (
    <ManageLayout
      title={t('manage.price_times')}
      subtitle={t('price_times.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')} disabled={!data}>
          {t('price_times.add')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : data.price_rules.length === 0 ? (
        <EmptyState
          icon="clock"
          title={t('price_times.empty')}
          body={t('price_times.empty_body')}
          action={
            <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
              {t('price_times.add')}
            </Button>
          }
        />
      ) : (
        <div className="list-card" role="list">
          {data.price_rules.map((r) => {
            const on = moment ? ruleOpen(r, moment) : false;
            return (
              <div key={r.id} role="listitem" className={`list-row${r.active ? '' : ' archived'}`}>
                <button className="list-main" onClick={() => setEditing(r)}>
                  <span className="list-title">{r.name}</span>
                  <span className="list-sub">
                    <Icon name="clock" size={14} />
                    <span>
                      {daysText(r.days)} · {r.start_time === r.end_time ? t('price_times.all_day') : `${r.start_time}–${r.end_time}`} · {forText(r)}
                      {places.multi && r.locations?.length ? ` · ${r.locations.map((id) => places.name(id)).join(', ')}` : ''}
                    </span>
                  </span>
                </button>
                <span className="list-price">{r.kind === 'percent' ? `−${r.percent}%` : money(r.price)}</span>
                {on ? (
                  <StatusBadge tone="ok" icon="checkCircle" label={t('price_times.on_now')} />
                ) : r.active ? (
                  <StatusBadge tone="neutral" icon="clock" label={t('price_times.waiting')} />
                ) : (
                  <StatusBadge tone="neutral" icon="pause" label={t('price_times.off')} />
                )}
                <Switch
                  checked={r.active}
                  label={r.active ? t('price_times.switch_off', { name: r.name }) : t('price_times.switch_on', { name: r.name })}
                  onChange={(v) => void toggle(r, v)}
                />
              </div>
            );
          })}
        </div>
      )}
      {editing && data ? (
        <RuleDialog
          rule={editing === 'new' ? null : editing}
          catalog={data}
          places={places.multi ? places.all.filter((p) => p.active || (editing !== 'new' && editing.locations?.includes(p.id))) : []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

function RuleDialog({
  rule,
  catalog,
  places,
  onClose,
  onSaved,
}: {
  rule: PriceRule | null;
  catalog: Catalog;
  /** The locations to choose from, when there is more than one. */
  places: Place[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { busy, run } = useSave();
  const [name, setName] = useState(rule?.name ?? t('price_times.default_name'));
  const [days, setDays] = useState<number[]>(rule?.days ?? [1, 2, 3, 4, 5]);
  const [start, setStart] = useState(rule?.start_time ?? '17:00');
  const [end, setEnd] = useState(rule?.end_time ?? '19:00');
  const [kind, setKind] = useState<PriceRule['kind']>(rule?.kind ?? 'percent');
  const [percent, setPercent] = useState(rule ? String(rule.percent || '') : '20');
  const [price, setPrice] = useState(rule && rule.kind === 'price' ? priceText(rule.price) : '');
  const [scope, setScope] = useState<'all' | 'some'>(rule && (rule.products.length || rule.categories.length) ? 'some' : 'all');
  const [cats, setCats] = useState<string[]>(rule?.categories ?? []);
  const [prods, setProds] = useState<string[]>(rule?.products ?? []);
  const [from, setFrom] = useState(rule?.date_from ?? '');
  const [to, setTo] = useState(rule?.date_to ?? '');
  const [where, setWhere] = useState<string[]>(rule?.locations ?? []);
  const [search, setSearch] = useState('');
  const [tried, setTried] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const pct = Number(percent.replace(',', '.'));
  const fixed = parseMoney(price);
  const errors = {
    name: name.trim() ? null : t('form.required'),
    days: days.length ? null : t('price_times.need_day'),
    value: kind === 'percent' ? (pct > 0 && pct <= 100 ? null : t('price_times.need_percent')) : fixed === null ? t('menu.price_invalid') : null,
    scope: scope === 'some' && !cats.length && !prods.length ? t('price_times.need_items') : kind === 'price' && scope === 'all' ? t('price_times.fixed_needs_items') : null,
    dates: from && to && from > to ? t('price_times.dates_order') : null,
  };
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.products.filter((p) => (q ? p.name.toLowerCase().includes(q) : true)).sort((a, b) => a.name.localeCompare(b.name));
  }, [catalog, search]);

  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(
      () =>
        action('products.save_price_rule', {
          ...(rule ? { id: rule.id } : {}),
          name: name.trim(),
          active: rule?.active ?? true,
          days,
          start_time: start,
          end_time: end,
          kind,
          percent: kind === 'percent' ? pct : 0,
          price: kind === 'price' ? fixed : 0,
          categories: scope === 'some' ? cats : [],
          products: scope === 'some' ? prods : [],
          date_from: from || null,
          date_to: to || null,
          locations: where,
        }),
      t('menu.saved', { name: name.trim() }),
    ).then((ok) => ok && onSaved());
  };
  const remove = () =>
    void run(() => action('products.delete_price_rule', { id: rule!.id }), t('price_times.deleted', { name: rule!.name })).then((ok) => ok && onSaved());

  const overnight = start > end;
  return (
    <Dialog
      title={rule ? rule.name : t('price_times.add')}
      onClose={onClose}
      footer={
        <>
          {rule ? (
            <Button variant="ghost" icon="trash" onClick={() => setDeleting(true)} disabled={busy}>
              {t('common.delete')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {rule ? t('common.save') : t('price_times.add')}
          </Button>
        </>
      }
    >
      <Field label={t('menu.name')} error={tried ? errors.name : null}>
        <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
      </Field>

      <fieldset className="option-group">
        <legend>{t('price_times.days')}</legend>
        <div className="chips" role="group" aria-label={t('price_times.days')}>
          {WEEK.map((d) => {
            const on = days.includes(d);
            return (
              <button key={d} className={`chip${on ? ' active' : ''}`} aria-pressed={on} onClick={() => setDays((x) => (on ? x.filter((y) => y !== d) : [...x, d]))}>
                {t(DAY_KEYS[d])}
              </button>
            );
          })}
        </div>
        {tried && errors.days ? <p className="text-danger">{errors.days}</p> : null}
      </fieldset>

      <div className="form-grid">
        <Field label={t('price_times.from')}>
          <input className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field label={t('price_times.to')} hint={start === end ? t('price_times.all_day_hint') : overnight ? t('price_times.overnight_hint') : undefined}>
          <input className="input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
      </div>

      <fieldset className="option-group">
        <legend>{t('price_times.price')}</legend>
        <div className="segmented" role="radiogroup" aria-label={t('price_times.price')}>
          <button role="radio" aria-checked={kind === 'percent'} className={kind === 'percent' ? 'active' : ''} onClick={() => setKind('percent')}>
            {t('price_times.kind_percent')}
          </button>
          <button role="radio" aria-checked={kind === 'price'} className={kind === 'price' ? 'active' : ''} onClick={() => setKind('price')}>
            {t('price_times.kind_price')}
          </button>
        </div>
        <div className="form-grid">
          {kind === 'percent' ? (
            <Field label={t('price_times.percent')} error={tried ? errors.value : null}>
              <input className="input" inputMode="decimal" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="20" />
            </Field>
          ) : (
            <Field label={t('price_times.fixed')} hint={t('price_times.fixed_hint')} error={tried ? errors.value : null}>
              <input className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
            </Field>
          )}
        </div>
      </fieldset>

      <fieldset className="option-group">
        <legend>{t('price_times.for')}</legend>
        <div className="segmented" role="radiogroup" aria-label={t('price_times.for')}>
          <button role="radio" aria-checked={scope === 'all'} className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>
            {t('price_times.whole_menu')}
          </button>
          <button role="radio" aria-checked={scope === 'some'} className={scope === 'some' ? 'active' : ''} onClick={() => setScope('some')}>
            {t('price_times.some')}
          </button>
        </div>
        {scope === 'some' ? (
          <div className="rule-scope">
            <span className="field-label">{t('menu.categories')}</span>
            <div className="chips">
              {catalog.categories.map((c) => {
                const on = cats.includes(c.id);
                return (
                  <button key={c.id} className={`chip${on ? ' active' : ''}`} aria-pressed={on} onClick={() => setCats((x) => (on ? x.filter((y) => y !== c.id) : [...x, c.id]))}>
                    {on ? <Icon name="check" size={14} /> : null} {c.name}
                  </button>
                );
              })}
            </div>
            <span className="field-label">{t('price_times.items')}</span>
            <div className="search">
              <Icon name="search" size={18} />
              <input className="input" placeholder={t('menu.search')} aria-label={t('menu.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="rule-items" role="group" aria-label={t('price_times.items')}>
              {items.map((p) => {
                const on = prods.includes(p.id);
                const viaCat = !!p.category && cats.includes(p.category);
                return (
                  <button
                    key={p.id}
                    role="checkbox"
                    aria-checked={on || viaCat}
                    disabled={viaCat}
                    className={`option${on || viaCat ? ' active' : ''}`}
                    onClick={() => setProds((x) => (on ? x.filter((y) => y !== p.id) : [...x, p.id]))}
                  >
                    <span className="option-check">{on || viaCat ? <Icon name="check" size={16} /> : null}</span>
                    <span className="option-name">{p.name}</span>
                    <span className="option-price">{money(p.price)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        {tried && errors.scope ? <p className="text-danger">{errors.scope}</p> : null}
      </fieldset>

      {places.length ? (
        <fieldset className="option-group">
          <legend>{t('price_times.where')}</legend>
          <div className="chips" role="group" aria-label={t('price_times.where')}>
            <button className={`chip${where.length === 0 ? ' active' : ''}`} aria-pressed={where.length === 0} onClick={() => setWhere([])}>
              {where.length === 0 ? <Icon name="check" size={14} /> : null} {t('price_times.everywhere')}
            </button>
            {places.map((pl) => {
              const on = where.includes(pl.id);
              return (
                <button key={pl.id} className={`chip${on ? ' active' : ''}`} aria-pressed={on} onClick={() => setWhere((x) => (on ? x.filter((y) => y !== pl.id) : [...x, pl.id]))}>
                  {on ? <Icon name="check" size={14} /> : <Icon name="store" size={14} />} {pl.name}
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <details className="more-fields" open={!!(rule?.date_from || rule?.date_to)}>
        <summary>{t('price_times.only_between')}</summary>
        <div className="form-grid">
          <Field label={t('price_times.first_day')}>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('price_times.last_day')} error={tried ? errors.dates : null}>
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </details>

      {deleting ? (
        <Confirm
          title={t('price_times.delete_title', { name: rule!.name })}
          body={t('price_times.delete_body')}
          confirm={t('common.delete')}
          danger
          onConfirm={remove}
          onClose={() => setDeleting(false)}
        />
      ) : null}
    </Dialog>
  );
}
